import paths from "~/constants/paths";
import { ApiError } from "~/types/Data/Common/APIs";

export async function useApi<T, E = ApiError>(
  url: string,
  params: any = {},
  options?: {
    baseUrl?: string | null;
    catchError?: boolean;
  },
): Promise<{ res?: T; err?: E }> {
  const { $baseUrl } = useNuxtApp();
  const localeCookie = useCookie("locale-cookie");
  const userToken = useCookie("hs-token");
  const cartCookie = useCookie("hs-cart");

  const router = useRouter();

  // Validate and set global headers
  const globalHeaders: Record<string, string> = {
    "Accept-Language": localeCookie.value ?? "en",
  };

  // Conditionally add Authorization header if provided
  if (params.authToken) {
    globalHeaders["Authorization"] = `Bearer ${params.authToken}`;
  }

  // Merge global headers with additional headers from params
  const headers: Record<string, string> = {
    ...globalHeaders,
    ...(params.headers || {}),
  };

  // Remove any invalid characters from headers
  for (const key in headers) {
    if (headers[key] && typeof headers[key] === "string") {
      headers[key] = headers[key].replace(/[^\x20-\x7E]/g, "");
    }
  }

  const opts = {
    ...params,
    headers,
  };

  try {
    const res = (await $fetch<T>(
      `${options?.baseUrl ?? $baseUrl}${url}`,
      opts,
    )) as T;
    return { res, err: undefined };
  } catch (err: any) {
    if (err.data.meta.errors[0].type === "AuthenticationException") {
      userToken.value = null;
      cartCookie.value = null;
      await router.replace(paths.login);
    } else {
      if (options?.catchError) return { res: undefined, err: err.data };
    }

    throw err;
  }
}
